import React from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Pencil } from "lucide-react";
import type { Profile, TicketMessage } from "@shared/schema";
import { Badge } from "@/components/ui/badge";
import { renderWithMentions } from "@/components/mention-textarea";
import { sanitizeEmailHtml } from "@/lib/email-client-helpers";
import { TicketInboundEmail } from "@/components/ticket-inbound-email";

export type TicketDiscussionMessage = TicketMessage & {
  modeleCode?: string | null;
  objet?: string | null;
  destinataire?: string | null;
  expediteur?: string | null;
  nbPiecesJointes?: number | null;
  corps?: string | null;
};

export function TicketDiscussionEntry({
  message,
  emailModelLabel,
  profiles,
  canAdmin,
  currentProfileId,
  onEdit,
  onReplyToClient,
}: {
  message: TicketDiscussionMessage;
  emailModelLabel?: string | null;
  profiles: Profile[];
  canAdmin: boolean;
  currentProfileId?: string | null;
  onEdit?: (message: TicketDiscussionMessage) => void;
  onReplyToClient?: (message: TicketDiscussionMessage) => void;
}) {
  const isClientEmail = message.source === "email_client";
  const isInboundEmail = isClientEmail && message.direction === "entrant";
  const isPortalMessage = !isClientEmail && !message.auteurId;
  const canEdit = !isClientEmail
    && !isPortalMessage
    && (canAdmin || currentProfileId === message.auteurId);
  const initials = (message.auteur || "Email")
    .split(" ")
    .map(word => word[0]?.toUpperCase())
    .join("")
    .slice(0, 2);
  const authorProfile = message.auteurId
    ? profiles.find(profile => profile.id === message.auteurId)
    : null;
  const authorName = authorProfile
    ? `${authorProfile.firstName} ${authorProfile.lastName}`.trim()
    : "—";
  const kind = isInboundEmail
    ? "email-entrant"
    : isClientEmail
      ? "email-sortant"
      : isPortalMessage
        ? "portail"
        : "interne";

  return (
    <div
      className={`flex gap-3 group rounded-md p-2 ${
        isInboundEmail
          ? "border border-teal-200 bg-teal-50/70 dark:border-teal-900 dark:bg-teal-950/20"
          : isClientEmail
            ? "border border-blue-200 bg-blue-50/60 dark:border-blue-900 dark:bg-blue-950/20"
            : isPortalMessage
              ? "border border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20"
              : ""
      }`}
      data-testid={`message-${message.id}`}
      data-message-kind={kind}
    >
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
          isInboundEmail
            ? "bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-200"
            : isClientEmail
              ? "bg-blue-100 text-blue-700"
              : isPortalMessage
                ? "bg-amber-100 text-amber-800"
                : "bg-primary/10 text-primary"
        }`}
      >
        {initials}
      </div>
      <div className="flex-1">
        {isInboundEmail ? (
          <TicketInboundEmail
            message={message}
            onReply={onReplyToClient ? () => onReplyToClient(message) : undefined}
          />
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {isClientEmail ? (
                <span className="font-semibold text-blue-800 dark:text-blue-300">
                  Email envoyé au client
                </span>
              ) : (
                <span className="font-medium text-foreground">{message.auteur}</span>
              )}
              {isPortalMessage && (
                <Badge variant="outline" className="border-amber-300 text-amber-800">
                  Message partenaire
                </Badge>
              )}
              <span>
                {message.createdAt
                  ? format(new Date(message.createdAt), "dd/MM/yyyy HH:mm", { locale: fr })
                  : ""}
              </span>
              {isClientEmail && <span className="sr-only">Email envoyé au client</span>}
              {message.editedAt && (
                <span className="italic text-muted-foreground/60">(modifié)</span>
              )}
              {canEdit && onEdit && (
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                  onClick={() => onEdit(message)}
                  data-testid={`button-edit-message-${message.id}`}
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </div>
            {isClientEmail ? (
              <div className="mt-2 space-y-1.5 text-sm" data-testid={`client-email-details-${message.id}`}>
                <p><span className="text-muted-foreground">Modèle :</span> <span className="font-medium">{emailModelLabel || "—"}</span></p>
                <p><span className="text-muted-foreground">Envoyé par :</span> <span className="font-medium">{authorName}</span></p>
                <p><span className="text-muted-foreground">Expéditeur :</span> {message.expediteur || "—"}</p>
                <p><span className="text-muted-foreground">Destinataire :</span> {message.destinataire || "—"}</p>
                {message.objet && <p className="font-medium">{message.objet}</p>}
                <details className="rounded border bg-background/70 p-2">
                  <summary className="cursor-pointer text-xs font-medium">Consulter le contenu</summary>
                  <div
                    className="prose prose-sm mt-2 max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(message.corps || message.contenu) }}
                  />
                </details>
              </div>
            ) : (
              <p className="text-sm mt-0.5 whitespace-pre-wrap">
                {renderWithMentions(message.contenu, profiles)}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}